/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth, useUser } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getActiveSubscription,
  getCustomerUpcomingInvoice,
  getSubscriptionTier,
  getSubscriptionType,
  isSubscriptionPaused,
  isSubscriptionPausedAndNotPremium,
  isSubscriptionPremium,
} from "helpers/subscription";
import { formatDateString } from "helpers/date";
import { formatCurrency } from "helpers/format";
import useClerkMetadata from "./useClerkMetadata";

function getSubscriptionQueryKey(userId?: string, customerId?: string) {
  return ["subscription", userId, customerId];
}

function getInvoiceQueryKey(userId?: string, customerId?: string) {
  return ["upcoming-invoice", userId, customerId];
}

export interface BillingInfo {
  interval: "year" | "month" | null;
  baseAmount: string | null; // Full subscription price without any discounts
  discountedAmount: string | null; // Price with discount applied (if discount is active)
  nextChargeAmount: string | null; // Actual amount that will be charged next (from Stripe)
  nextChargeDate: string | null; // When the next charge will occur
  isTrialing: boolean;
  trialEndDate: string | null;
  discountPercent: number | null;
  discountEndsWithin30Days: boolean;
  discountEndDate: string | null;
  isCancelled?: boolean;
  cancelDate?: string | null; // Date when the subscription will be cancelled
  isPaused?: boolean;
  pauseEndDate?: string | null; // Date when the subscription will resume from pause
}

//TODO(Jimmy): Move this to a Subscription feature with components/Subscription
function useSubscription() {
  const { userMetadata } = useClerkMetadata();
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();

  const customerId = userMetadata?.stripeCustomerId;
  const emailAddresses = user?.emailAddresses.map(
    (record) => record.emailAddress
  );

  const isEnabled = isSignedIn && isLoaded && !!user?.id && !!userMetadata;

  const subscriptionQuery = useQuery({
    queryKey: getSubscriptionQueryKey(user?.id, customerId),
    queryFn: () =>
      getActiveSubscription({
        customerId,
        emailAddresses,
      }),
    enabled: isEnabled,
  });

  const upcomingInvoiceQuery = useQuery({
    queryKey: getInvoiceQueryKey(user?.id, customerId),
    queryFn: () => getCustomerUpcomingInvoice(customerId),
    enabled: isEnabled && !!subscriptionQuery.data,
  });

  const subscriptionType = subscriptionQuery.data
    ? getSubscriptionType(subscriptionQuery.data)
    : undefined;

  const subscriptionTier = useMemo(() => {
    return getSubscriptionTier(subscriptionQuery.data);
  }, [subscriptionQuery.data]);

  const isFreePlan = useMemo(
    () => subscriptionTier === "free",
    [subscriptionTier]
  );

  const billingInfo = useMemo<BillingInfo>(() => {
    if (!subscriptionQuery.data?.user) {
      return {
        interval: null,
        baseAmount: null,
        discountedAmount: null,
        nextChargeAmount: null,
        nextChargeDate: null,
        isTrialing: false,
        trialEndDate: null,
        discountPercent: null,
        discountEndsWithin30Days: false,
        discountEndDate: null,
        isCancelled: false,
        cancelDate: null,
        isPaused: false,
        pauseEndDate: null,
      };
    }

    const userSub = subscriptionQuery.data.user;
    const interval = userSub.plan.interval as "year" | "month";
    const isTrialing = userSub.status === "trialing";

    // Calculate base amount (full price without discount)
    const baseAmountCents = userSub.plan.amount;
    const baseAmount = baseAmountCents
      ? formatCurrency(baseAmountCents / 100)
      : null;

    // Calculate discounted amount if discount is active
    const discountPercent = userSub.discount?.coupon?.percent_off;
    const discountedAmountCents =
      baseAmountCents && discountPercent
        ? baseAmountCents * (1 - discountPercent / 100)
        : null;
    const discountedAmount = discountedAmountCents
      ? formatCurrency(discountedAmountCents / 100)
      : null;

    // Get next charge amount and date from upcoming invoice if available
    // This is the most accurate as it accounts for all discounts, prorations, etc.
    const nextChargeAmount = upcomingInvoiceQuery.data?.amount_due
      ? formatCurrency(upcomingInvoiceQuery.data.amount_due / 100)
      : null;

    // Use upcoming invoice period_end if available, otherwise fall back to calculated date
    const nextChargeTimestamp = upcomingInvoiceQuery.data?.period_end
      ? upcomingInvoiceQuery.data.period_end
      : isTrialing
        ? userSub.trial_end
        : userSub.current_period_end;

    const nextChargeDate = nextChargeTimestamp
      ? formatDateString(new Date(nextChargeTimestamp * 1000), {
          month: "long",
        })
      : null;

    const trialEndDate = userSub.trial_end
      ? formatDateString(new Date(userSub.trial_end * 1000), { month: "long" })
      : null;

    // Calculate if discount ends within 30 days
    const discountEndTimestamp = userSub.discount?.end;
    const discountEndsWithin30Days = discountEndTimestamp
      ? discountEndTimestamp * 1000 - Date.now() <= 30 * 24 * 60 * 60 * 1000 &&
        discountEndTimestamp * 1000 - Date.now() > 0
      : false;

    const discountEndDate = discountEndTimestamp
      ? formatDateString(new Date(discountEndTimestamp * 1000), {
          month: "long",
        })
      : null;

    // Check if subscription is cancelled
    const isCancelled = userSub.cancel_at_period_end === true;
    const cancelDate = userSub.cancel_at
      ? formatDateString(new Date(userSub.cancel_at * 1000), {
          month: "long",
        })
      : null;

    // Check if subscription is paused
    const isPaused = !!userSub.pause_collection;
    const pauseEndDate = userSub.pause_collection?.resumes_at
      ? formatDateString(new Date(userSub.pause_collection.resumes_at * 1000), {
          month: "long",
        })
      : null;

    return {
      interval,
      baseAmount,
      discountedAmount,
      nextChargeAmount,
      nextChargeDate,
      isTrialing,
      trialEndDate,
      discountPercent: discountPercent || null,
      discountEndsWithin30Days,
      discountEndDate,
      isCancelled,
      cancelDate,
      isPaused,
      pauseEndDate,
    };
  }, [subscriptionQuery.data, upcomingInvoiceQuery.data]);

  return {
    subscription: isSignedIn ? subscriptionQuery.data : undefined,
    upcomingInvoice: isSignedIn ? upcomingInvoiceQuery.data : undefined,
    isLoading: subscriptionQuery.isLoading || upcomingInvoiceQuery.isLoading,
    isError: subscriptionQuery.isError || upcomingInvoiceQuery.isError,
    error: subscriptionQuery.error || upcomingInvoiceQuery.error,
    isSubscriptionLoaded: subscriptionQuery.isSuccess,
    isSubscriptionPremium: subscriptionQuery.isSuccess
      ? isSubscriptionPremium(subscriptionQuery.data)
      : undefined,
    isSubscriptionPaused: subscriptionQuery.isSuccess
      ? isSubscriptionPaused(subscriptionQuery.data?.user)
      : undefined,
    isSubscriptionPausedAndNotPremium: subscriptionQuery.isSuccess
      ? isSubscriptionPausedAndNotPremium(subscriptionQuery.data?.user)
      : undefined,
    subscriptionTier,
    subscriptionType,
    isFreePlan,
    billingInfo,
  };
}

export default useSubscription;
